import { Action } from 'redux';
import { select, put, delay, all } from 'redux-saga/effects';
import { BasketActionTypes, BasketActions } from '../';
import { ErrorAction, AppState } from 'store';
import {
    UpsellModel,
    AddUpsellOptionModel,
    UpsellTypes,
    BasketItemModel,
    AvailableSofaglideModel,
    SOFARESCUE_CODE,
    ProductTypes,
    FREE_FABRIC_CARE_KIT_NAME
} from 'models/Basket';
import { ACTION_CREATOR_NAME, analyticsHelper, errorToBasketErrorModel, productHelper } from 'helpers';
import { addProductSaga } from './addProduct';
import { api } from 'repositories/api';
import { initBasketPageSaga } from './initBasketPage';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';

const ADD_UPSELL_MIN_DELAY = 2500;

export interface AddUpsellAction extends Action<BasketActionTypes.ADD_UPSELL> {
    upsellItem: UpsellModel;
    selectedOption: AddUpsellOptionModel;
}

export interface AddUpsellActionStart extends Action<BasketActionTypes.ADD_UPSELL_START> {
    upsellItem: UpsellModel;
    selectedOption: AddUpsellOptionModel;
}

export interface AddUpsellActionSuccess extends Action<BasketActionTypes.ADD_UPSELL_SUCCESS> {
    upsellType: UpsellTypes;
}

export interface AddUpsellActionError extends ErrorAction<BasketActionTypes.ADD_UPSELL_ERROR> {
    upsellType: UpsellTypes;
}

export const addUpsell: (upsellItem: UpsellModel, selectedOption: AddUpsellOptionModel) => AddUpsellAction =
    (upsellItem: UpsellModel, selectedOption: AddUpsellOptionModel) => ({
        type: BasketActionTypes.ADD_UPSELL,
        upsellItem,
        selectedOption
    });
addUpsell[ACTION_CREATOR_NAME] = 'addUpsell';

export type AddUpsellFunc = (...args: Parameters<typeof addUpsell>) => void;

export function* addUpsellSaga({ upsellItem, selectedOption }: AddUpsellAction) {
    yield put<BasketActions>({
        type: BasketActionTypes.ADD_UPSELL_START,
        upsellItem,
        selectedOption
    });
}

export function* addUpsellQueueSaga({ upsellItem, selectedOption }: AddUpsellActionStart): BasketSagaReturnType {
    try {
        yield all([
            addUpsellToOrder(upsellItem, selectedOption),
            delay(ADD_UPSELL_MIN_DELAY)
        ]);

        yield callBasketSaga(initBasketPageSaga, {
            type: BasketActionTypes.INIT_BASKET_PAGE
        });

        yield put<BasketActions>({
            type: BasketActionTypes.ADD_UPSELL_SUCCESS,
            upsellType: upsellItem.type
        });

        return true;
    } catch (error) {
        yield put<BasketActions>({
            type: BasketActionTypes.ADD_UPSELL_ERROR,
            upsellType: upsellItem.type,
            error: errorToBasketErrorModel(error, 'addUpsellQueueSaga')
        });

        return false;
    }
}

function* addUpsellToOrder(upsellItem: UpsellModel, selectedOption: AddUpsellOptionModel) {
    switch (upsellItem.type) {
        case UpsellTypes.Sofashield:
            yield addSofashield(selectedOption);
            break;
        case UpsellTypes.FabricCareKit:
            yield addFabricCareKit(selectedOption);
            break;
        case UpsellTypes.LeatherCareKit:
            yield addLeatherCareKit(selectedOption);
            break;
        case UpsellTypes.Sofaglide:
            yield addSofaglide();
            break;
        case UpsellTypes.SofaRescue:
            yield addSofaRescue();
            break;
        default:
            break;
    }
}

function* addSofashield(selectedOption: AddUpsellOptionModel) {
    if (selectedOption.code) {
        yield callBasketSaga(addProductSaga, {
            type: BasketActionTypes.ADD_PRODUCT,
            code: selectedOption.code,
            quantity: selectedOption.quantity
        });
    }
}

function* addFabricCareKit(selectedOption: AddUpsellOptionModel) {
    const state: AppState = yield select();
    const fabricCareKits = productHelper.getLoadedFabricCareKits(state.basketState.loadedCareKits);

    for (const fabricCareKit of fabricCareKits) {
        if (fabricCareKit.isRecommended && fabricCareKit.name !== FREE_FABRIC_CARE_KIT_NAME) {
            yield callBasketSaga(addProductSaga, {
                type: BasketActionTypes.ADD_PRODUCT,
                code: fabricCareKit.code,
                quantity: selectedOption.quantity
            });
        }
    }
}

function* addLeatherCareKit(selectedOption: AddUpsellOptionModel) {
    const state: AppState = yield select();
    const leatherCareKits = productHelper.getLoadedLeatherCareKits(state.basketState.loadedCareKits);

    for (const leatherCareKit of leatherCareKits) {
        if (leatherCareKit.isRecommended) {
            yield callBasketSaga(addProductSaga, {
                type: BasketActionTypes.ADD_PRODUCT,
                code: leatherCareKit.code,
                quantity: selectedOption.quantity
            });
        }
    }
}

function* addSofaglide() {
    const state: AppState = yield select();
    const basketItems = state.basketState.basket?.entries!;
    const filteredEntries: BasketItemModel[] = basketItems.filter((productEntry: BasketItemModel) =>
        productEntry?.availableSofaglides?.length && !productEntry.linkedSofaglide);

    for (const entryToAdd of filteredEntries) {
        const availableSofaglide: AvailableSofaglideModel = entryToAdd.availableSofaglides[0];

        yield callBasketSaga(addProductSaga, {
            type: BasketActionTypes.ADD_PRODUCT,
            code: availableSofaglide.crossSellingProductCode,
            quantity: entryToAdd.quantity,
            sofaglideParentCode: availableSofaglide.baseProductCode
        });

        sendAnalytics(availableSofaglide.crossSellingProductCode, entryToAdd.quantity, true);
    }
}

function* addSofaRescue() {
    yield callBasketSaga(addProductSaga, {
        type: BasketActionTypes.ADD_PRODUCT,
        code: SOFARESCUE_CODE,
        quantity: 1
    });
}

function sendAnalytics(code: string, addedQuantity: number, isSofaglide: boolean): void {
    analyticsHelper.withLogging(() => {
        // in this saga - only for Sofashields and Sofaglides
        // other upsells are processing in addProduct saga

        isSofaglide
            ? api.google.addSofaglideProduct(code, addedQuantity)
            : api.google.addAdditionalProduct(ProductTypes.Sofashield, code, addedQuantity);
    }, 'addSofaglide');
}
